import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as ChevronDownSvg } from "@assets/Icons/IconChevron-Down.svg";
import {
  usePartnerCustomerSelection,
  PARTNER_ACCOUNT_VALUE,
} from "@shared/navigation/SideNav/usePartnerCustomerSelection";
import {
  getTenantCardSubLine,
  shouldShowTenantCard,
} from "../../sideMenuVisibility";
import MobileBottomSheet from "./MobileBottomSheet";
import PanelSpinner from "./PanelSpinner";
import { usePanelReveal } from "../../hooks/usePanelReveal";
import {
  CHEVRON_ICON,
  CHROME_TRIGGER,
  PANEL_OPEN_DOWN,
  ROW_GAP,
  ROW_ICON,
  ROW_PADDING,
  ROW_RADIUS,
  ROW_TEXT,
  CHROME_SUBLINE_STRIP,
  SUBLINE_TEXT,
} from "../../sideMenuDensity";

type Auth0Args = {
  isLoading: boolean;
  isAuthenticated: boolean;
  getAccessTokenSilently: () => Promise<string>;
};

export type SideMenuTenantCardProps = {
  tenantType: string;
  auth0: Auth0Args;
  /** When collapsed the card is hidden, but the hook still runs (keeps the
   *  partner/customer selection bootstrapped). */
  collapsed?: boolean;
  /** Mobile drawer variant: Partner (GLOBAL_ADMIN only) and Customer render as
   *  stacked rows, each opening its own full-width panel inline (instead of the
   *  desktop breadcrumb + side-by-side 2-column dropdown, too wide at 264px). */
  mobile?: boolean;
};

type Option = { label: string; value: string };


/**
 * One column of the combined dropdown: a header, a search box and a selectable
 * list. Shows a centered spinner in place of the list while `loading`. Both
 * columns use it, off their own flag: the Partner column while the partner list
 * is first fetched, the Customer column while a newly-picked partner's customers
 * load. Without it a column renders its "No options" empty state mid-flight,
 * which reads as "this tenant has none" rather than "not loaded yet".
 *
 * `revealing` is the cosmetic sibling of that: the same ring, held briefly after
 * the panel opens over a list that is already in hand. It renders identically but
 * silently — a reveal announces nothing, because no fetch is happening. Whenever
 * both are true the real `loading` wins, so a cold open is announced.
 */
function SelectColumn({
  title,
  options,
  selectedValue,
  onSelect,
  loading = false,
  revealing = false,
  showItemChevron = true,
  fullWidth = false,
}: {
  title: string;
  options: Option[];
  selectedValue?: string;
  onSelect: (value: string) => void;
  loading?: boolean;
  /** Panel just opened: hold the list behind a silent spinner. */
  revealing?: boolean;
  /** Trailing `›` per row — indicates a deeper level. Off for customers (leaf). */
  showItemChevron?: boolean;
  /** Full-width, self-sizing column (mobile inline panel) instead of the fixed
   *  `w-60` / `h-full` desktop column. */
  fullWidth?: boolean;
}) {
  const [query, setQuery] = useState("");
  const filtered = query.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  /* Spinner or list, resolved here rather than as a ternary inside the JSX. The
     column has a fixed height, so nothing shifts when one replaces the other. */
  const renderListSlot = () => {
    if (loading || revealing) {
      return (
        <div
          className={`flex items-center justify-center ${
            fullWidth ? "h-40" : "flex-1"
          }`}
        >
          {/* Silent unless a fetch is genuinely in flight — see the docblock. */}
          <PanelSpinner className="h-5 w-5" decorative={!loading} />
        </div>
      );
    }

    return (
      <ul
        className={`scrollbar-hide overflow-auto px-2 py-1 ${
          fullWidth ? "max-h-56" : "min-h-0 flex-1"
        }`}
      >
        {filtered.length === 0 ? (
          <li className={`${ROW_PADDING} ${ROW_TEXT} text-semantic-text-secondary`}>
            No options
          </li>
        ) : (
          filtered.map((opt) => {
            const active = opt.value === selectedValue;
            return (
              <li key={opt.value}>
                <button
                  type="button"
                  onClick={() => onSelect(opt.value)}
                  className={`flex w-full items-center justify-between ${ROW_GAP} ${ROW_RADIUS} border ${ROW_PADDING} text-left ${ROW_TEXT} transition-colors ${
                    active
                      ? "border-semantic-border-primary bg-semantic-state-active-brand text-semantic-text-primary"
                      : "border-transparent text-semantic-text-secondary hover:bg-semantic-state-active hover:text-semantic-text-brand"
                  }`}
                >
                  <span className="truncate" title={opt.label}>
                    {opt.label}
                  </span>
                  {showItemChevron && (
                    <ChevronDownSvg className={`${CHEVRON_ICON} shrink-0 -rotate-90`} />
                  )}
                </button>
              </li>
            );
          })
        )}
      </ul>
    );
  };

  return (
    <div className={`flex flex-col ${fullWidth ? "w-full" : "h-full w-60"}`}>
      <div className="px-2 pb-1.5 pt-2 text-[10px] font-bold text-semantic-text-secondary">
        {title}
      </div>
      <div className="px-2 pb-1.5">
        <div className={`flex ${CHROME_TRIGGER} w-full items-center gap-2 rounded-sm border border-semantic-border-secondary bg-semantic-bg-primary-alt px-2 py-1`}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search..."
            className={`min-w-0 flex-1 bg-transparent ${ROW_TEXT} text-semantic-text-primary outline-none placeholder-gray-400`}
          />
        </div>
      </div>
      {renderListSlot()}
    </div>
  );
}

/**
 * Tenant card (below the account card). The current tenant context shows as ONE
 * trigger: a `Partner › Customer` breadcrumb for a GLOBAL_ADMIN, collapsed to just
 * the customer for a PARTNER (which never spans more than its own partner). Clicking
 * it opens a combined dropdown with two side-by-side columns — Partner (GLOBAL_ADMIN
 * only) and Customer — each with its own search + list. Selecting a partner keeps
 * the dropdown open and shows a spinner in the Customer column while that
 * partner's customers load; the tenant context itself only moves once a customer
 * (or "All customers") is picked, so closing the dropdown midway changes nothing.
 * A sub-line shows the Partner id (brand strip) in
 * partner view — GLOBAL_ADMIN only, the sole tenant type that receives it — or the
 * selected customer's id (muted strip). Hidden for CUSTOMER tenants and when
 * collapsed.
 */
export default function SideMenuTenantCard({
  tenantType,
  auth0,
  collapsed = false,
  mobile = false,
}: SideMenuTenantCardProps) {
  const pc = usePartnerCustomerSelection({
    tenantType,
    isLoading: auth0.isLoading,
    isAuthenticated: auth0.isAuthenticated,
    getAccessTokenSilently: auth0.getAccessTokenSilently,
  });

  // `open` drives the desktop combined dropdown; `openSeg` drives the mobile
  // per-segment (Partner / Customer) inline panels.
  const [open, setOpen] = useState(false);
  const [openSeg, setOpenSeg] = useState<null | "partner" | "customer">(null);
  const rootRef = useRef<HTMLDivElement>(null);

  /* One reveal per panel rather than per column: the desktop dropdown opens as a
     single surface, so its two columns fill in together. The mobile sheets open
     independently, so each keeps its own. */
  const revealingDropdown = usePanelReveal(open);
  const revealingPartnerSheet = usePanelReveal(openSeg === "partner");
  const revealingCustomerSheet = usePanelReveal(openSeg === "customer");

  /**
   * Dismiss the desktop dropdown without a completed choice: any partner the user
   * was browsing is discarded, leaving the tenant context on the partner actually
   * in use. Not used on the commit path — `onSelectCustomer` clears the preview
   * itself as part of applying it.
   */
  const dismissDropdown = () => {
    setOpen(false);
    pc.cancelPartnerPreview();
  };

  // Close any open dropdown / panel on outside click.
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      // Mobile panels are bottom sheets (portaled outside rootRef); their own
      // backdrop closes them, so skip the window outside-click there.
      if (mobile || (!open && !openSeg)) return;
      if (rootRef.current?.contains(e.target as Node)) return;
      dismissDropdown();
      setOpenSeg(null);
    };
    window.addEventListener("pointerdown", onPointerDown, { capture: true });
    return () =>
      window.removeEventListener(
        "pointerdown",
        onPointerDown as EventListener,
        { capture: true } as EventListenerOptions
      );
  }, [open, openSeg, mobile]);

  // First option is the "Partner Account" sentinel → relabel as "All customers".
  const customerMenuOptions: Option[] = useMemo(
    () =>
      pc.customerOptions.map((o, i) =>
        i === 0 ? { ...o, label: "All customers" } : o
      ),
    [pc.customerOptions]
  );

  /* The rule itself lives in `shouldShowTenantCard` because the chrome above has
     to reserve the right seam based on the same answer — see CHROME_SEAM_ABOVE_NAV. */
  if (!shouldShowTenantCard({ tenantType, collapsed })) return null;

  /**
   * Only a GLOBAL_ADMIN spans several partners, so only they get the partner
   * surfaces: the breadcrumb prefix, the Partner column and the mobile Partner row.
   * A PARTNER is always scoped to itself — repeating its own name would just cost
   * room the customer needs (same reasoning as the Partner id sub-line, CTX-176).
   */
  const canSwitchPartner = tenantType === "GLOBAL_ADMIN";
  const hasPartner = !!pc.partnerName;
  const showPartnerScope = canSwitchPartner && hasPartner;
  const inPartnerView = pc.customerSelectedValue === PARTNER_ACCOUNT_VALUE;
  const customerLabel = inPartnerView
    ? "All customers"
    : pc.customerDisplay || "All customers";

  // Sub-line: Partner id (brand strip) while in partner view, Customer id (muted)
  // once a customer is selected — or nothing at all when the tenant never receives
  // that identifier (see getTenantCardSubLine). Both ids come from the tenant's
  // chargebeeCustomerId.
  const subLine = getTenantCardSubLine({
    tenantType,
    inPartnerView,
    partnerCode: pc.partnerCode,
    customerId: pc.customerId,
  });

  const subLineStrip = subLine && (
    <div
      className={`flex ${CHROME_SUBLINE_STRIP} items-center border-t px-2 ${SUBLINE_TEXT} ${
        subLine.brand
          ? "border-semantic-border-secondary bg-semantic-bg-brand-primary font-medium text-semantic-text-brand"
          : "border-semantic-border-secondary bg-semantic-bg-secondary text-semantic-text-secondary"
      }`}
    >
      {subLine.text}
    </div>
  );

  // Mobile: Partner and Customer as two stacked rows; each opens its own
  // full-width bottom sheet (the desktop 2-column dropdown is too wide at 264px).
  if (mobile) {
    const segChevron = (seg: "partner" | "customer") =>
      `${CHEVRON_ICON} shrink-0 text-semantic-text-secondary transition-transform ${
        openSeg === seg ? "" : "-rotate-90"
      }`;
    const rowClass =
      `flex w-full items-center justify-between ${ROW_GAP} ${ROW_PADDING} ${ROW_TEXT} text-semantic-text-primary transition-colors hover:bg-semantic-state-active`;
    return (
      <div
        ref={rootRef}
        className="overflow-hidden rounded-lg border border-semantic-border-secondary bg-semantic-bg-primary"
      >
        {/* Partner row — a switcher, hence GLOBAL_ADMIN only. */}
        {showPartnerScope && (
          <div className="border-b border-semantic-border-secondary">
            <button
              type="button"
              onClick={() =>
                setOpenSeg((s) => (s === "partner" ? null : "partner"))
              }
              aria-expanded={openSeg === "partner"}
              className={rowClass}
            >
              <span className="min-w-0 truncate" title={pc.partnerName}>
                {pc.partnerName}
              </span>
              <ChevronDownSvg className={segChevron("partner")} />
            </button>
          </div>
        )}

        {/* Customer row */}
        <button
          type="button"
          onClick={() =>
            setOpenSeg((s) => (s === "customer" ? null : "customer"))
          }
          aria-expanded={openSeg === "customer"}
          className={rowClass}
        >
          {pc.isFetching ? (
            <PanelSpinner className={ROW_ICON} />
          ) : (
            <span className="min-w-0 truncate" title={customerLabel}>
              {customerLabel}
            </span>
          )}
          <ChevronDownSvg className={segChevron("customer")} />
        </button>

        {subLineStrip}

        {/* Partner sheet (GLOBAL_ADMIN only) */}
        {canSwitchPartner && (
          <MobileBottomSheet
            open={openSeg === "partner"}
            onClose={() => setOpenSeg(null)}
          >
            <SelectColumn
              fullWidth
              title="Partner"
              options={pc.partnerOptionsById}
              selectedValue={pc.partnerSelectedValue}
              loading={pc.isPartnerListLoading}
              revealing={revealingPartnerSheet}
              showItemChevron={false}
              onSelect={(v) => {
                // Close the sheet while the new partner's customers load.
                pc.selectPartnerById(v);
                setOpenSeg(null);
              }}
            />
          </MobileBottomSheet>
        )}

        {/* Customer sheet */}
        <MobileBottomSheet
          open={openSeg === "customer"}
          onClose={() => setOpenSeg(null)}
        >
          <SelectColumn
            fullWidth
            title="Customer"
            options={customerMenuOptions}
            selectedValue={pc.customerSelectedValue}
            loading={pc.isCustomerListLoading}
            revealing={revealingCustomerSheet}
            showItemChevron={false}
            onSelect={(v) => {
              pc.onSelectCustomer(v);
              setOpenSeg(null);
            }}
          />
        </MobileBottomSheet>
      </div>
    );
  }

  return (
    /* Two elements on purpose. The outer one is a bare positioning context for the
       dropdown; the border, the fill and the rounding sit on the inner box so it can
       carry `overflow-hidden`. That clip is what makes the sub-line strip's bottom
       corners follow the card's inner radius instead of poking past it: nesting a
       `rounded-b-lg` child inside a `rounded-lg` 1px border gives the child a corner
       curve wider than the border's inner curve, so its tint bled into the corner.

       `overflow-hidden` cannot go on the outer element — the dropdown is absolutely
       positioned and would be clipped away the moment it opened. */
    <div ref={rootRef} className="relative">
      <div
        className={`overflow-hidden rounded-lg border ${
          open
            ? "border-semantic-border-brand-secondary bg-semantic-state-active-brand"
            : "border-semantic-border-secondary bg-semantic-bg-primary"
        }`}
      >
      {/* Trigger: the whole Partner › Customer breadcrumb opens the dropdown. */}
      <button
        type="button"
        onClick={() => (open ? dismissDropdown() : setOpen(true))}
        aria-haspopup="menu"
        aria-expanded={open}
        /* No radius of its own: the wrapper's `overflow-hidden` clips it, which also
           retires the `subLine && !open` conditional that used to pick between
           `rounded-t-lg` and `rounded-lg` depending on what rendered beneath it. */
        className={`flex ${CHROME_TRIGGER} w-full items-center gap-1 px-2 ${ROW_TEXT} text-semantic-text-primary transition-colors hover:bg-semantic-state-active`}
      >
        {showPartnerScope && (
          <>
            <span
              className="max-w-[90px] shrink-0 truncate"
              title={pc.partnerName}
            >
              {pc.partnerName}
            </span>
            <ChevronDownSvg
              className={`${CHEVRON_ICON} shrink-0 -rotate-90 text-semantic-text-secondary`}
            />
          </>
        )}
        {/* The spinner stands in for the customer label, so it takes the row-icon
            size rather than the chevron's. */}
        {pc.isFetching ? (
          <PanelSpinner className={ROW_ICON} />
        ) : (
          <span
            className="min-w-0 flex-1 truncate text-left"
            title={customerLabel}
          >
            {customerLabel}
          </span>
        )}
      </button>

      {/* Sub-line: hidden while the dropdown is open so it opens flush under the
          breadcrumb (matching the design). */}
      {!open && subLineStrip}
      </div>

      {/* Combined dropdown: Partner | Customer columns side by side. Tall enough to
          browse a real partner/customer list without scrolling row by row, clamped to
          the viewport so it still fits on short screens (the card sits near the top of
          the menu, so the panel grows downwards). */}
      {open && (
        <div
          className={`absolute left-0 top-full z-[70] mt-1 flex h-96 max-h-[60vh] divide-x divide-semantic-border-secondary rounded-md border border-semantic-border-secondary bg-semantic-bg-primary shadow-lg ${PANEL_OPEN_DOWN}`}
        >
          {canSwitchPartner && (
            <SelectColumn
              title="Partner"
              options={pc.partnerOptionsById}
              selectedValue={pc.partnerSelectedValue}
              loading={pc.isPartnerListLoading}
              revealing={revealingDropdown}
              onSelect={(v) => {
                // Stay open: the Customer column spins while this partner's
                // customers load, and the switch is applied by the customer pick.
                pc.previewPartnerById(v);
              }}
            />
          )}
          <SelectColumn
            // Remount per partner so a leftover search query can't hide the new
            // partner's customers.
            key={pc.partnerSelectedValue}
            title="Customer"
            options={customerMenuOptions}
            selectedValue={pc.customerSelectedValue}
            loading={pc.isCustomerListLoading}
            revealing={revealingDropdown}
            showItemChevron={false}
            onSelect={(v) => {
              pc.onSelectCustomer(v);
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}
